import React, { useCallback, useState } from 'react';
import { Layout } from 'components/common/layout/Layout';
import { PageHeader } from '../components/sections/PageHeader';
import { Leaderboard, LeaderboardCard } from '../components/leaderboards/LeaderboardCard';
import { GetStaticProps } from 'next';
import csv from 'csvtojson';
import { container } from 'tsyringe';
import { FileService } from '../server/services/file.service';
import { buildFileUrlOrNull } from '../client/images.utils';
import { TableRow, TableColumn, TableData } from '../components/common/Table';
import { asyncForEach } from '../shared/shared.utils';
import { AdminGQLService } from '../server/services/admingql.service';

export default function Leaderboards({ leaderboards }: { leaderboards: Leaderboard[] }) {
    const [selectedLeaderboard, setSelectedLeaderboard] = useState<Leaderboard>(leaderboards[0] ?? undefined);

    const onChangeDropdown = useCallback((val: Leaderboard) => {
        setSelectedLeaderboard(val);
    }, []);

    const noLeaderboardsText = 'There are no leaderboards available currently. Please check back later!';

    return (
        <Layout>
            <PageHeader
                title={selectedLeaderboard?.infoTitle ?? 'Leaderboards'}
                description={selectedLeaderboard?.infoContents ?? noLeaderboardsText}
                className="leaderboards-page-header"
            />
            <div className="container">
                {!!selectedLeaderboard && (
                    <LeaderboardCard
                        leaderboards={leaderboards}
                        selectedLeaderboard={selectedLeaderboard}
                        onChangeDropdown={onChangeDropdown}
                    />
                )}
            </div>
        </Layout>
    );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
    const fileService = container.resolve(FileService);
    const response = await fileService.getLeaderboardData();
    const adminGQLService = container.resolve(AdminGQLService);

    if (response) {
        const leaderboards: Leaderboard[] = [];
        await asyncForEach(response.espin_leaderboard, async (leaderboard) => {
            try {
                // construct our table data for leaderboard here
                const request = require('request');
                const filePath = buildFileUrlOrNull(leaderboard.file);
                if (!!filePath) {
                    const json = await csv().fromStream(request.get(filePath));
                    const columns: TableColumn[] = [];
                    const rows: TableRow[] = [];
                    Object.keys(json[0]).forEach((col) => {
                        const tableCol: TableColumn = {
                            columnName: col,
                        };
                        columns.push(tableCol);
                    });

                    const spinNamesToFind: string[] = [];
                    await asyncForEach(json, async (tRow) => {
                        const row: TableRow = {
                            columnData: [],
                            hyperlinkData: [],
                        };
                        const colData: string[] = [];
                        const hlData: string[] = [];
                        // doing a call per user is very slow. aggregate the names + modify it after the fact.
                        await asyncForEach(Object.values(tRow), async (data: string, idx: number) => {
                            // SPIN Username column has to have hyperlinks

                            if (columns[idx].columnName === 'SPIN Username') {
                                // the column includes the SPIN username, but this is not always the slug
                                // we must access the slug from the database, given the SPIN username
                                spinNamesToFind.push(data);
                            }
                            // prefill the hl data here
                            hlData.push(null);
                            colData.push(data);
                        });
                        row.columnData = colData;
                        row.hyperlinkData = hlData;
                        rows.push(row);
                    });
                    // are there names to gather from the db?

                    if (spinNamesToFind.length > 0) {
                        const allNamesProfiles = await adminGQLService.BulkGetPlayerSlugFromSPINUsername({
                            spin_usernames: spinNamesToFind,
                        });
                        const spinNameColumn = columns.indexOf(columns.find((c) => c.columnName === 'SPIN Username'));

                        // ok - now for each row
                        for (const r of rows) {
                            const slugFound = allNamesProfiles.espin_player_profile.find(
                                (p) => p.spin_username === r.columnData[spinNameColumn],
                            );
                            if (slugFound) {
                                r.hyperlinkData[spinNameColumn] = `./players/${slugFound.slug}`;
                            }
                        }
                    }
                    const tableData: TableData = {
                        columns,
                        rows,
                    };
                    const lb: Leaderboard = {
                        name: leaderboard.name,
                        infoTitle: leaderboard.info_title,
                        infoContents: leaderboard.info_contents,
                        dropdownName: leaderboard.dropdown_label,
                        tableData,
                    };
                    leaderboards.push(lb);
                }
            } catch (e) {
                console.error(e);
            }
        });

        return {
            props: {
                leaderboards,
            },
            revalidate: 300,
        };
    }

    return {
        props: {
            leaderboards: [],
        },
        revalidate: 300,
    };
};
